import * as React from 'react';
import * as Semantic from 'semantic-ui-react';
import * as Styles from './phoneticsmodal.styles';
import { LexemeContents } from './LexemeContents';
import { Spinner } from 'components/spinner';
import { LanguageCode } from '@visikon/core-models/languageTypes';
import { useDeleteLexeme, usePhoneticsList, useUpsertLexeme } from 'api/phoneticsApi';
import { ObjectOmit } from 'commons/utils';
import { ErrorModal } from './ErrorModal';
import { LexemeCreationEntry, LexemeEntry } from '@visikon/core-models';

export type SelectLexemeFn = (lexeme: LexemeCreationEntry | null) => void;

type LexemeFilter = 'none' | 'missing-metadata' | 'multiple-entries';

type Props = {
  activeLexeme?: LexemeCreationEntry;
  onSelectLexeme: SelectLexemeFn;
  lang: LanguageCode;
};

export default function PhoneticsList({ activeLexeme, onSelectLexeme, lang }: Props) {
  const [filter, setFilter] = React.useState<LexemeFilter>('none');
  const { data: lexemeItems, isLoading } = usePhoneticsList(lang);
  const upsertMutation = useUpsertLexeme();

  const sortedLexemeItems = React.useMemo(() => {
    if (!lexemeItems) return [];

    const graphemes = lexemeItems.reduce<Record<string, LexemeEntry[]>>((acc, lexme) => {
      if (!acc[lexme.grapheme]) {
        acc[lexme.grapheme] = [];
      }
      acc[lexme.grapheme].push(lexme);
      return acc;
    }, {});

    return lexemeItems
      .sort((a, b) => {
        const aText = a.grapheme;
        const bText = b.grapheme;
        return aText.localeCompare(bText, lang);
      })
      .map((lexme) => {
        const graphemeGroup = graphemes[lexme.grapheme];
        return {
          ...lexme,
          isPrimary: graphemeGroup[0].id === lexme.id,
          isOnlyEntry: graphemeGroup.length === 1,
          isMissingInfo: !lexme.stem?.trim() || !lexme.description?.trim(),
        };
      });
  }, [lexemeItems, lang]);

  const visibleLexemeItems = React.useMemo(() => {
    if (filter === 'none') return sortedLexemeItems;

    return sortedLexemeItems.filter((lexeme) => {
      if (filter === 'missing-metadata') return lexeme.isMissingInfo;
      if (filter === 'multiple-entries') return !lexeme.isOnlyEntry;
      return true;
    });
  }, [filter, sortedLexemeItems]);

  const deleteLexemeMutation = useDeleteLexeme();
  const handleSelectLexeme = (lexeme: LexemeCreationEntry | null) => {
    onSelectLexeme(lexeme ?? null);
  };

  const handleDeleteLexeme = (lexeme: LexemeEntry) => {
    deleteLexemeMutation.mutate({
      id: lexeme.id,
      language: lang,
    });
  };

  const handleMakePrimaryLexeme = (lexeme: (typeof sortedLexemeItems)[number]) => {
    if (lexeme.isMissingInfo) {
      // eslint-disable-next-line no-alert
      alert('Please fill in the stem and description before marking this lexeme primary.');
      return;
    }

    upsertMutation.mutate({
      lexeme: { ...lexeme, primary: true },
      language: lang,
    });
  };

  const isLoadingState = deleteLexemeMutation.isLoading || isLoading;

  return (
    <>
      {deleteLexemeMutation.isError && <ErrorModal>Failed to delete lexeme: {deleteLexemeMutation?.error?.message ?? 'unknown error'}</ErrorModal>}
      {upsertMutation.isError && <ErrorModal>Failed to make lexeme primary: {upsertMutation?.error?.message ?? 'unknown error'}</ErrorModal>}
      <Spinner visible={isLoadingState} />
      <Styles.TableWrapper>
        <Semantic.Table compact>
          <Semantic.Table.Header>
            <Semantic.Table.Row>
              <Semantic.Table.HeaderCell>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Lexemes</span>
                  <Semantic.Dropdown
                    compact
                    inline
                    direction="left"
                    options={[
                      { key: 'none', text: 'No filter', value: 'none' },
                      { key: 'missing-metadata', text: 'Missing metadata', value: 'missing-metadata' },
                      { key: 'multiple-entries', text: 'Multiple lexemes', value: 'multiple-entries' },
                    ]}
                    value={filter}
                    onChange={(_event, data) => {
                      setFilter((data.value as LexemeFilter) ?? 'none');
                    }}
                  />
                </div>
              </Semantic.Table.HeaderCell>
            </Semantic.Table.Row>
          </Semantic.Table.Header>

          <Semantic.Table.Body>
            {visibleLexemeItems.map((lexme) => (
              <Styles.PhoneticRow key={lexme.id} onClick={() => handleSelectLexeme(lexme)} selected={activeLexeme?.id === lexme.id}>
                <Semantic.Table.Cell style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <LexemeContents lexeme={lexme} />
                  <Styles.PhoneticActionContainer>
                    {!lexme.isOnlyEntry && !lexme.isPrimary && (
                      <Styles.PhoneticEntryActionIcon
                        name="arrow up"
                        title="Mark as primary lexeme for this grapheme"
                        onClick={(ev: React.MouseEvent) => {
                          ev.stopPropagation();
                          handleMakePrimaryLexeme(lexme);
                        }}
                      />
                    )}
                    <Styles.PhoneticEntryActionIcon
                      name="copy outline"
                      title="Create new lexeme with same grapheme and phoneme"
                      onClick={(ev: React.MouseEvent) => {
                        ev.stopPropagation();
                        handleSelectLexeme(ObjectOmit(lexme, 'id'));
                      }}
                    />
                    <Styles.PhoneticEntryActionIcon color="red" name="trash" title="Delete lexeme" onClick={() => handleDeleteLexeme(lexme)} />
                  </Styles.PhoneticActionContainer>
                </Semantic.Table.Cell>
              </Styles.PhoneticRow>
            ))}
          </Semantic.Table.Body>
          <Styles.StickyTableFooter>
            <Semantic.Table.Row>
              <Semantic.Table.HeaderCell>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Semantic.Button size="tiny" icon="plus" content="New entry" onClick={() => handleSelectLexeme(null)} />
                  <span>{visibleLexemeItems.length} entries</span>
                </div>
              </Semantic.Table.HeaderCell>
            </Semantic.Table.Row>
          </Styles.StickyTableFooter>
        </Semantic.Table>
      </Styles.TableWrapper>
    </>
  );
}
